import { AxiosError } from 'axios';
import { IRecentSearchForm } from '../features/search/searchTypes';
import { EFeedbackType } from '../features/contact-us/contactUsTypes';
import { authApiV1 } from '../axiosWrapper';
import { EmailService } from '../services/emailService';
import { showSuccessAlert, showErrorAlert } from '../services/globalAlertService';
import { LogLevel, trackExceptionWithContext } from '../services/application-insights/appInsights';
import { AppInsightsComponents, AppInsightsErrorTypes } from '../services/application-insights/appInsightsConstants';
import { store } from '../core/store'; // Your Redux store
import { selectSignedInUserInfo } from '../user-management/appSelectors';

export interface ISupportRequestUserInfo {
    userId?: number;
    email?: string;
    fullName?: string;
    company?: string;
}

export const horizonAppLoginUrl = window.config.SHELL_APP_URL;

export const redirectToSignIn = () => {
	window.location.href = `${horizonAppLoginUrl}/account/login?redirect=${encodeURIComponent(window.location.href)}`;
};

export const redirectToMsiMainWebsite = () => {
	window.location.href = window.config.MAIN_WEBSITE_URL;
};

export const storeRecentSearchesInLocalStorage = (searchInfo: IRecentSearchForm[]): void => {
	localStorage.setItem('recentSearches', JSON.stringify(searchInfo));
};

export const getRecentSearchesFromLocalStorage = (): IRecentSearchForm[] => {
	const storedData = localStorage.getItem('recentSearches');
	if (!storedData || storedData === '[]') {
		return [] as IRecentSearchForm[];
	}
	const parsedList = JSON.parse(storedData);
	return parsedList as IRecentSearchForm[];
};

export const clearRecentSearchesFromLocalStorage = (): void => {
	localStorage.removeItem('recentSearches');
};

export const handleAxiosError = (error: AxiosError) => {
	return {
		message: error.message,
		name: error.name,
		stack: error.stack,
	};
};

export const getCurrentDateTimeValue = (): string => {
	const formatDateValue = (date: Date): string => {
		const day = String(date.getDate()).padStart(2, '0');
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const year = date.getFullYear();
		return `${day}/${month}/${year}`;
	};

	const formatTimeValue = (date: Date): string => {
		const hours = String(date.getHours()).padStart(2, '0');
		const minutes = String(date.getMinutes()).padStart(2, '0');
		const seconds = String(date.getSeconds()).padStart(2, '0');
		return `${hours}:${minutes}:${seconds}`;
	};

	const now = new Date();
	return `${formatDateValue(now)} ${formatTimeValue(now)}`;
};

// From DCF and FMV (Value) it comes as Mn default 
export const formatAmount = (value: number): string => {
	if (value >= 1_000) {

		return formatValueAsString(value / 1_000, { minimumFractionDigits: 1, maximumFractionDigits: 1 }, '', '$', ' Bn');
	}
	return formatValueAsString(value, { minimumFractionDigits: 1, maximumFractionDigits: 1 }, '', '$', ' Mn');

};

export const formatValueAsString = (value: number | string | null | undefined, options: Intl.NumberFormatOptions | null = {}, nullOrEmptyValue: string = '', startContent: string = '', endContent: string = ''): string => {

	if (Number.isNaN(value)) {
		return nullOrEmptyValue;
	}

	if (typeof value === 'string') {
		const tempVal = Number(value);
		if (isNaN(tempVal)) {
			return value.toString();
		}
		value = tempVal;
	}

	if (value === null || value === undefined) {
		return nullOrEmptyValue;
	}
	const locale = (window.navigator as any).languages ? (window.navigator as any).languages[0] : ((window.navigator as any).language || 'en');

	if (startContent !== '' || endContent !== '') {
		return startContent + value.toLocaleString(locale, options || {}) + endContent;
	}
	return value.toLocaleString(locale, options || {});
}

export const formatBigNumbersToString = (value: number) => {
	if (value >= 1000) {
		return `${(value / 1000).toFixed(0)}K`;
	}
	return value.toString();
}


export const formatDateAsString = (date: string | null | undefined, options: Intl.DateTimeFormatOptions | null = {}, nullOrEmptyValue: string = ''): string => {
	if (date === null || date === undefined) {
		return nullOrEmptyValue;
	}
	// let defaultOptions: Intl.DateTimeFormatOptions= { year: 'numeric', month: 'numeric', day: 'numeric'};
	// if(!isNil(options)) {
	// 	defaultOptions = { ...defaultOptions, ...{options} };		
	// }
	const locale = (window.navigator as any).languages ? (window.navigator as any).languages[0] : ((window.navigator as any).language || 'en');
	return new Date(date).toLocaleDateString(locale, options || {});
}

export interface IDeviceInfo {
	userAgent: string;
	os: string;
	browser: string;
	screenWidth: number;
	screenHeight: number;
	devicePixelRatio: number;
	language: string;
	timezone: string;
}

/**
 * Collects comprehensive device and browser information
 * @returns Object containing device info and formatted string
 */
export const getDeviceInfo = (): { info: IDeviceInfo; formatted: string } => {
	const userAgent = navigator.userAgent;
	
	const os = userAgent.includes('Windows') ? 'Windows' :
		userAgent.includes('Macintosh') ? 'Mac' :
			userAgent.includes('Linux') ? 'Linux' :
				userAgent.includes('Android') ? 'Android' :
					userAgent.includes('iOS') ? 'iOS' : 'Unknown OS';
	
	const browser = userAgent.includes('Edg') ? 'Edge' : // Edge Chromium
		userAgent.includes('OPR') || userAgent.includes('Opera') ? 'Opera' :
			userAgent.includes('Chrome') ? 'Chrome' :
				userAgent.includes('Firefox') ? 'Firefox' :
					userAgent.includes('Safari') ? 'Safari' :
						userAgent.includes('MSIE') || userAgent.includes('Trident') ? 'Internet Explorer' :
							'Unknown Browser';

	// Additional device info
	const screenWidth = window.screen.width;
	const screenHeight = window.screen.height;
	const devicePixelRatio = window.devicePixelRatio;
	const language = navigator.language || (navigator.languages && navigator.languages[0]) || 'unknown';
	const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown';

	const info: IDeviceInfo = {
		userAgent,
		os,
		browser,
		screenWidth,
		screenHeight,
		devicePixelRatio,
		language,
		timezone
	};

	const formatted = `User Agent: ${userAgent}\nOS: ${os}\nBrowser: ${browser}\nScreen: ${screenWidth}x${screenHeight} @${devicePixelRatio}x\nLanguage: ${language}\nTimezone: ${timezone}`;

	return { info, formatted };
};

// Send Error Feedback
export async function sendErrorFeedback(error: any) {
	const formData = new FormData();
	formData.append("feedbackType", EFeedbackType.Error);
	formData.append("subject", error.name);
	const message = error.message + 'Details:\n\n' + error.stack;

	const { formatted: userInfo } = getDeviceInfo();

	formData.append("message", message + '\n\n' + userInfo);

	try {
		const apiUrl = window.config?.HORIZON_API_URL;
		const response = await authApiV1.post(apiUrl + "/account/SendFeedbackV2", formData, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
		});
		if (response.status === 200 && response.data.success) {
			alert('Your feedback has been sent successfully!');
			return;
		}
		else {
			throw new Error("Failed to send feedback. Please try again.");
		}
	}
	catch (error: any) {
		// console.log(JSON.stringify(error));
	} finally {
		redirectToMsiMainWebsite();
	}
}


export async function sendSupportRequestToMsi(
    subject: string, 
    message: string, 
    userInfo?: ISupportRequestUserInfo
) {
    try {
        const { formatted: deviceInfo } = getDeviceInfo();
        
        // Build user information section
        const userInfoSection = userInfo ? `
            <hr>
            <h4>User Information:</h4>
            <table style="border-collapse: collapse; width: 100%; font-size: 14px;">
                ${userInfo.fullName ? `<tr><td style="padding: 4px; font-weight: bold; width: 120px;">Name:</td><td style="padding: 4px;">${userInfo.fullName}</td></tr>` : ''}
                ${userInfo.email ? `<tr><td style="padding: 4px; font-weight: bold;">Email:</td><td style="padding: 4px;">${userInfo.email}</td></tr>` : ''}
                ${userInfo.userId ? `<tr><td style="padding: 4px; font-weight: bold;">User ID:</td><td style="padding: 4px;">${userInfo.userId}</td></tr>` : ''}                
                ${userInfo.company ? `<tr><td style="padding: 4px; font-weight: bold;">Company:</td><td style="padding: 4px;">${userInfo.company}</td></tr>` : ''}
            </table>
        ` : '';
        
        const fullMessage = `
            <div style="font-family: Arial, sans-serif;">
                <h3>Support Request</h3>
				<p><strong>Subject:</strong> ${subject}</p>
                <div>
                    <strong>Message:</strong>
                    <div style="margin: 10px 0; padding: 10px; background-color: #f5f5f5; border-left: 3px solid #007acc;">
                        ${message.replace(/\n/g, '<br>')}
                    </div>
                </div>
                
                ${userInfoSection}
                
                <hr>
                <h4>Device Information:</h4>
                <pre style="background-color: #f9f9f9; padding: 10px; border-radius: 4px; font-size: 12px;">
${deviceInfo}
                </pre>
                
                <hr>
                <p style="font-size: 12px; color: #666;">
                    <strong>Timestamp:</strong> ${getCurrentDateTimeValue()}<br/>
                    <strong>Application:</strong> MSI Seascape
                </p>
            </div>
        `;

        const result = await EmailService.sendSingleEmail({
            recipient: 'webmaster@msiltd.com',
            subject: `Support Request: ${subject}${userInfo?.fullName ? ` - ${userInfo.fullName}` : ''}`,
            body: fullMessage,
            isHtml: true
        });

        if (result.isSuccess) {
            // Use your existing alert service instead of SharedAlert
            showSuccessAlert('Your support request has been sent successfully!');
            return { success: true, emailId: result.emailId };
        } else {
            throw new Error(result.errorMessage || 'Failed to send support request');
        }
    } catch (error: any) {
        // Use your existing alert service instead of SharedAlert
		showErrorAlert(`Failed to send support request: ${error.message}`);
        return { success: false, error: error.message };
    }
}

export const getUserInfoFromStore = (): ISupportRequestUserInfo | undefined => {
    try {
        const state = store.getState();
        const user = selectSignedInUserInfo(state);
        
        if (user) {
            return {
                userId: user.id,
                email: user.emailAddress,
                fullName: user.fullName,
				company: user.clientName
            };
        }
    } catch (error) {
         trackExceptionWithContext(
            error instanceof Error ? error : new Error('Failed to get user info from store'),
            {
                component: AppInsightsComponents.AXIOS_SEASCAPE_INTERCEPTOR,
                action: 'get-user-info-from-store-error',
                severity: LogLevel.Warning,
                additionalData: {
                    errorType: AppInsightsErrorTypes.CONFIGURATION_ERROR,
                    context: 'axios-interceptor-500-handler',
                    storeAvailable: !!store,
                    errorMessage: error instanceof Error ? error.message : String(error)
                }
            }
        );
    }
    return undefined;
};

export const formatDateForISO = (date: Date): string => {
	const dateValue = new Date(date);
	const year = dateValue.getFullYear();
	const month = String(dateValue.getMonth() + 1).padStart(2, '0');
	const day = String(dateValue.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
};

export const extractDateFromISO = (isoString: string): string => {
	// Extract date portion from ISO string (YYYY-MM-DDTHH:mm:ssZ -> YYYY-MM-DD)
	return isoString.split('T')[0];
};

export const isValidDateString = (dateString: string): boolean => {
	const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;
	if (!isoDateRegex.test(dateString)) {
		return false;
	}

	const date = new Date(dateString);
	return date instanceof Date && !isNaN(date.getTime()) && dateString === formatDateForISO(date);
};



// Helper function to get cookie value
export const setCookie = (cname: string, emailAddress: string) => {
    const expires = getCookieExpiration();
    const domain = getCookieDomainName();
    document.cookie = `${cname}= ${JSON.stringify({ emailAddress })}; ${expires}; path=/; domain=${domain}`;
};


const getCookieDomainName = () => {
    return window.config?.COOKIE_DOMAIN || window.location.hostname;
}

const getCookieExpiration = () => {
    const now = new Date();

    const expirationMilliseconds = 60 * 60 * 1000; // 1 hour
    now.setTime(now.getTime() + expirationMilliseconds);
    const expires = 'expires=' + now.toUTCString();
    return expires;
}


// Helper function to clean labels for use in keys and Redux state (for routing consistency)
export const cleanLabelForKey = (label: string): string => {
	// remove all special characters
	label = label.replace(/[^a-zA-Z0-9]/g, '').trim().toLowerCase();
	return label;
};